import { useEffect, useRef, useState } from 'react';
import { ensureFreshAccessTokenForStream } from '@shared/api/ensureFreshAccessTokenForStream';
import { useNotificationStore } from '../store/notificationStore';
import {
  openNotificationEventStream,
  type ConnectionState,
  type NotificationFrame,
} from '../lib/notificationEventStream';

/**
 * N5 (86d3w2kg2) — owns the single notification SSE connection, and the only place frames become
 * store state.
 *
 * ⚠️ Mount this EXACTLY ONCE, in `AppLayout`. It is the first app-lifetime stream in this codebase —
 * every other one is page-scoped — so a second mount is a second EventSource against a browser that
 * allows about six per origin.
 */

/**
 * A module-scoped fan-out beside the store, not instead of it.
 *
 * The store answers "what is true now"; this answers "something just arrived". They are different
 * questions and only one of them survives a diff: N8's toasts and deep-link flash need the ARRIVAL,
 * and reconstructing that by diffing store snapshots is both fiddly and wrong across a batched frame.
 * It costs a few lines now and saves that.
 */
const eventTarget = new EventTarget();
const FRAME_EVENT = 'csc:notification-frame';

export function onNotificationFrame(handler: (frame: NotificationFrame) => void): () => void {
  const listener = (e: Event) => handler((e as CustomEvent<NotificationFrame>).detail);
  eventTarget.addEventListener(FRAME_EVENT, listener);
  return () => eventTarget.removeEventListener(FRAME_EVENT, listener);
}

/**
 * N8's reconnect-suppression seam — the flag that ticket asked N5's client API to name explicitly.
 *
 * True while the client is catching up after a dropped connection or a hidden tab. `notify()` consults
 * it so a laptop waking from sleep does not throw twenty toasts at its owner.
 *
 * ⚠️ <b>Today this is belt AND braces, and it should stay that way.</b> The catch-up path is
 * `resync()`, which fetches a page and folds it into the store WITHOUT dispatching frames — so the
 * toast layer already sees nothing during a reconnect, by construction. This flag exists because that
 * is a property of how catch-up currently happens to work, not a guarantee: the day the server starts
 * replaying missed events over the stream (`Last-Event-ID`), frames WILL arrive on reconnect and the
 * suppression has to be here already. ⛔ Do not remove it as redundant.
 */
// A COUNTER, not a boolean: two catch-ups can overlap (the visibility-reopen hydrate plus the new
// handle's own onReconnect if the fresh stream blips), and a boolean's first `finally` would lower
// the shield while the second catch-up still runs (2026-08-25 review).
let hydrating = 0;

export function isHydratingNotifications(): boolean {
  return hydrating > 0;
}

/** Runs a catch-up with the hydrating shield raised — lowered in `finally`, so a throw cannot pin it. */
async function hydrate(run: () => Promise<void>): Promise<void> {
  hydrating += 1;
  try {
    await run();
  } finally {
    hydrating -= 1;
  }
}

/**
 * Routes one frame into the store.
 *
 * Each of the three shapes has its own path deliberately — folding them into one would lose the
 * property each exists for: `counts_changed` must touch nothing but the four numbers, and
 * `notifications.changed` must be ONE state update rather than N.
 */
function applyFrame(frame: NotificationFrame) {
  const store = useNotificationStore.getState();

  switch (frame.type) {
    case 'upsert':
      store.upsert(frame.notification, frame.counts);
      return;
    case 'counts_changed':
      store.applyCounts(frame.counts);
      return;
    case 'notifications.changed':
      store.applyBatch(frame.notifications ?? [], frame.counts);
      return;
    default:
      // A frame type this client does not know yet. Ignoring it is correct — the server may ship a
      // new one ahead of a client release, and the next page load renders the same truth anyway.
      return;
  }
}

export function useNotificationEventStream(): { connectionState: ConnectionState } {
  const [connectionState, setConnectionState] = useState<ConnectionState>('connecting');
  // Strict mode double-invokes effects in dev; the ref stops that opening two EventSources.
  const handleRef = useRef<{ close: () => void } | null>(null);

  useEffect(() => {
    // Visibility gating, adopted from the quotation and gate streams (E7 item 4). It matters MORE
    // here than there: this stream is app-lifetime, so without it every idle background tab holds a
    // connection open against a ~6-per-origin budget and starves the foreground one.
    //
    // Missing frames while hidden is safe precisely because reopening runs the catch-up below.
    const open = () => {
      if (handleRef.current) return;
      handleRef.current = openNotificationEventStream({
        onFrame: (frame) => {
          applyFrame(frame);
          eventTarget.dispatchEvent(new CustomEvent<NotificationFrame>(FRAME_EVENT, { detail: frame }));
        },
        onConnectionStateChange: setConnectionState,
        // ⚠️ SILENT. A reconnect merge must never toast — a user who alt-tabs back does not want to
        // be told their connection blipped, and `resync()` merges rather than resetting so their
        // scroll position survives.
        onReconnect: () => {
          void hydrate(() => useNotificationStore.getState().resync());
        },
      });
    };

    const close = () => {
      handleRef.current?.close();
      handleRef.current = null;
    };

    let disposed = false;

    const onVisibility = () => {
      if (document.visibilityState !== 'visible') {
        close();
        return;
      }

      // Refresh BEFORE reopening. This path calls connect() directly rather than going through the
      // stream's own scheduleReconnect(), so it does not inherit that refresh — and a tab hidden past
      // the 15-minute expiry is exactly the case that needs it.
      void ensureFreshAccessTokenForStream().then(() => {
        if (disposed || document.visibilityState !== 'visible') return;
        open();
        // A close/reopen is a gap just like a dropped connection, but the stream's own onReconnect
        // only fires for reconnects within one handle's life — so the catch-up has to be fired here
        // too, or the feed stays silently stale for whatever it missed while hidden.
        void hydrate(() => useNotificationStore.getState().resync());
      });
    };

    close(); // Strict-mode double-invoke: never leave a previous handle open.
    // The first open needs no refresh — AuthInitializer already refreshes a stale token at mount.
    if (document.visibilityState === 'visible') open();
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      disposed = true;
      document.removeEventListener('visibilitychange', onVisibility);
      close();
    };
  }, []);

  return { connectionState };
}
